'use client'

import { GoogleAnalytics } from '@next/third-parties/google'
import { useCookieValue } from '@react-hookz/web/useCookieValue/index.js'
import { Button } from '@snowball-tech/fractal/dist/src/components/Button'
import { Paper } from '@snowball-tech/fractal/dist/src/components/Paper'
import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'
import { UilTimes as IconClose } from '@tooni/iconscout-unicons-react'
import { Analytics } from '@vercel/analytics/next'
import { SpeedInsights } from '@vercel/speed-insights/next'
import { useSearchParams } from 'next/navigation'

import { type ReactNode, useEffect } from 'react'

import isEmpty from 'lodash/fp/isEmpty'

import type { ExtendedUser } from '../types'

import {
  LOCAL_DOMAIN,
  PREPROD_DOMAIN,
  SNOWBALL_DOMAIN,
  SNOWBALL_GOOGLE_ANALYTICS_ID,
} from '../constants'
import { isDev, isPreview, isProd } from '../helpers'
import useTranslations from '../hooks/useTranslations'
import useUser from '../hooks/useUser'
import IntercomProvider from '../providers/IntercomProvider'
import CrazyEgg from './CrazyEgg'
import GoogleTagManager from './GoogleTagManager'
import IntercomButton from './IntercomButton'

type Props = {
  withCrazyEgg?: boolean
  withGoogleAnalytics?: boolean
  withGoogleTagManager?: boolean
  withIntercom?: boolean
  withVercelAnalytics?: boolean
  withVercelSpeedInsights?: boolean
}
export default function GDPRCompliance({
  withCrazyEgg = false,
  withGoogleAnalytics = false,
  withGoogleTagManager = false,
  withIntercom = false,
  withVercelAnalytics = false,
  withVercelSpeedInsights = false,
}: Props) {
  const searchParams = useSearchParams()
  const $t = useTranslations()

  const forceConsent =
    (searchParams.has('gdpr_consent') &&
      searchParams.get('gdpr_consent') !== 'false') ||
    !isProd()

  const { user } = useUser<ExtendedUser>()

  const [gdprConsent, setConsent] = useCookieValue('gdpr-consent', {
    domain: isDev()
      ? LOCAL_DOMAIN
      : isPreview()
        ? PREPROD_DOMAIN
        : SNOWBALL_DOMAIN,
    // The consent is valid for 6 months.
    expires: Math.round(365 / 2),
    sameSite: 'lax',
  })

  useEffect(() => {
    if (forceConsent) {
      setConsent('true')
    }
  }, [forceConsent, setConsent])

  if (gdprConsent === undefined && !forceConsent) {
    return false
  }

  const reallyWithIntercom =
    withIntercom &&
    (!isDev() ||
      (searchParams.has('force_intercom') &&
        searchParams.get('force_intercom') !== 'false'))

  if (gdprConsent === 'true' || forceConsent) {
    const reallyWithGoogleTagManager =
      withGoogleTagManager &&
      (!isDev() ||
        (searchParams.has('force_gtm') &&
          searchParams.get('force_gtm') !== 'false'))

    const reallyWithAnalytics =
      isProd() ||
      (searchParams.has('force_analytics') &&
        searchParams.get('force_analytics') !== 'false')

    const reallyWithGoogleAnalytics =
      !withGoogleTagManager &&
      withGoogleAnalytics &&
      reallyWithAnalytics &&
      !isEmpty(SNOWBALL_GOOGLE_ANALYTICS_ID)

    const reallyWithCrazyEgg =
      withCrazyEgg &&
      (!isDev() ||
        (searchParams.has('force_crazy_egg') &&
          searchParams.get('force_crazy_egg') !== 'false'))

    return (
      <>
        {reallyWithGoogleTagManager ? (
          <GoogleTagManager />
        ) : (
          reallyWithGoogleAnalytics && (
            <GoogleAnalytics gaId={SNOWBALL_GOOGLE_ANALYTICS_ID!} />
          )
        )}

        {withVercelSpeedInsights && reallyWithAnalytics && <SpeedInsights />}
        {withVercelAnalytics && reallyWithAnalytics && <Analytics />}

        {reallyWithIntercom && (
          <IntercomProvider user={user}>
            <IntercomButton />
          </IntercomProvider>
        )}

        {reallyWithCrazyEgg && <CrazyEgg />}
      </>
    )
  }

  if (reallyWithIntercom && gdprConsent === 'intercom-only') {
    return (
      <>
        <IntercomProvider user={user}>
          <IntercomButton />
        </IntercomProvider>
      </>
    )
  }

  // Components that can be added even when user didn't consent to the GDPR.
  const anonymousComponents: ReactNode = false

  if (gdprConsent === 'false') {
    return anonymousComponents
  }

  return (
    <>
      <Paper
        className="fixed bottom-3 left-3 z-50 w-fit max-w-[calc(100%_-_theme(spacing.6))] md:max-w-min"
        elevation="3"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-row justify-between">
            <Typography element="p" variant="heading-4">
              {$t('gdpr.banner.title')}
            </Typography>

            <Button
              icon={<IconClose />}
              iconOnly
              label={$t('gdpr.refuse.all')}
              variant="text"
              onClick={() => setConsent('false')}
            />
          </div>

          <div className="flex flex-col gap-1">
            <Typography variant="body-1-median">
              {$t('gdpr.banner.content')}
            </Typography>

            <Typography variant="caption-median">
              {$t.rich('gdpr.banner.privacy_policy')}
            </Typography>
          </div>

          <div className="flex gap-1 items-center md:justify-end flex-col md:flex-row w-full">
            <Button
              className="w-full md:w-auto"
              label={$t('gdpr.refuse.all')}
              variant="secondary"
              onClick={() => setConsent('false')}
            />

            <Button
              className="w-full md:w-auto"
              label={$t('gdpr.accept.essentials')}
              variant="secondary"
              onClick={() => setConsent('intercom-only')}
            />

            <Button
              className="w-full md:w-auto"
              label={$t('gdpr.accept.all')}
              variant="primary"
              onClick={() => setConsent('true')}
            />
          </div>
        </div>
      </Paper>

      {anonymousComponents}
    </>
  )
}
